import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const ProductShowcase = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const products = [
    {
      id: 1,
      title: 'Furniture',
      description: 'Accent | Bar | Kids | Pet | Outdoor',
      image: '/images/furnitureinto.jpg',
      link: '/products#furniture'
    },
    {
      id: 2,
      title: 'Table Decor & Utility',
      description: 'Candle Holders | Bath Sets | Utility Boxes | Tissue Boxes | Office Accessories',
      image: '/images/utilityintro.jpg',
      link: '/products#table-decor'
    },
    {
      id: 3,
      title: 'Serveware & Kitchenware',
      description: 'Trays | Platters | Bowls | Cake Stands | Boards | Coasters',
      image: '/images/servewareintro.jpg',
      link: '/products#serveware'
    },
    {
      id: 4,
      title: 'Wall Decor',
      description: 'Mirror | Frames | Wall Hooks | Shelves',
      image: '/images/walldecorintro.jpg',
      link: '/products#wall-decor'
    }
  ];

  return (
    <section className="py-10 xs:py-12 sm:py-16 md:py-20 bg-background">
      <motion.div
        className="container mx-auto px-3 xs:px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="text-center mb-8 xs:mb-12 sm:mb-16">
          <motion.h2 
            className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold mb-3 xs:mb-4 sm:mb-6 bg-gradient-primary bg-clip-text text-transparent"
            variants={cardVariants}
          >
            What We Offer
          </motion.h2>
          <motion.p 
            className="text-sm xs:text-base sm:text-lg md:text-xl text-text-light max-w-2xl mx-auto px-3 xs:px-4 leading-relaxed"
            variants={cardVariants}
          >
            Our work spans furniture, tableware, and lifestyle accessories — all built at our SEDEX-compliant facility with low MOQs and complete customizability.
            We work with Acacia, Mango, Oak, Pine, and pair them with stone or metal for unique finishes.
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 w-full"
          variants={containerVariants}
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              className="group relative overflow-hidden rounded-lg xs:rounded-xl sm:rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col lg:flex-row"
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              {/* Image Section */}
              <div className="w-full lg:w-1/2 aspect-[4/3] lg:aspect-square relative overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              
              {/* Content Section */}
              <div className="w-full lg:w-1/2 p-4 xs:p-5 sm:p-6 lg:p-8 flex flex-col justify-center bg-white">
                <h3 className="text-lg xs:text-xl sm:text-2xl font-bold mb-2 xs:mb-3 text-gray-800">{product.title}</h3>
                <p className="text-xs xs:text-sm sm:text-base text-gray-600 leading-relaxed">{product.description}</p>
                <div className="mt-3 xs:mt-4">
                  <Link
                    to={product.link}
                    className="text-primary font-semibold hover:text-primary/80 transition-colors inline-flex items-center text-xs xs:text-sm sm:text-base"
                  >
                    View Collection
                    <svg className="w-3.5 h-3.5 xs:w-4 xs:h-4 ml-1.5 xs:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Background Decorative Elements */}
      <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    </section>
  );
};

export default ProductShowcase;
 